import {
  ActivatedRoute,
  Router,
  RouterModule
} from '@angular/router';
import { CommonModule } from '@angular/common';

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  inject
} from '@angular/core';
import { SessionStorageService } from '../helpers/session-storage.service';
import { FormsModule } from '@angular/forms';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { ImageModule } from 'primeng/image';
import { TagModule } from 'primeng/tag';
import { ProgressBarModule } from 'primeng/progressbar';
import { finalize } from 'rxjs/operators';
import { AiBrokerChatService, HumanSupportRequestPayload } from './ai-broker-chat.service';
import { FcmService } from '../services/fcm.service';
import { ChatAttachment, ChatChartContent, ChatContent, ChatMessage, ChatParticipant, ChatTableContent, ChatActionButton, TransactionContext } from './ai-broker-chat.models';
import { AuthService } from '../auth/auth.service';
import { User as FirebaseUser } from '@angular/fire/auth';
import { firstValueFrom, Subscription } from 'rxjs';
import { StorageService } from '../services/storage.service';
import { ChatTableComponent } from './components/chat-table.component';
import { ChatChartComponent } from './components/chat-chart.component';
import { ChatLocationComponent } from './components/chat-location.component';
import { ChatContactComponent } from './components/chat-contact.component';
import { ChatImageComponent } from './components/chat-image.component';
import { ChatAudioComponent } from './components/chat-audio.component';
import { ChatVideoComponent } from './components/chat-video.component';
import { LanguageService } from '../services/language.service';
import { TranslateModule } from '@ngx-translate/core';

type ChatMessageView = ChatMessage & { contents: ChatContent[] };
type CanvasReferencePrefix = 'REQ' | 'TRK' | 'SHP' | 'MTCH';

interface CanvasReference {
  fullRef: string;
  prefix: CanvasReferencePrefix;
  id: string;
  routeToken: string;
}

@Component({
  selector: 'app-ai-broker-chat',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    AvatarModule,
    ButtonModule,
    TextareaModule,
    TooltipModule,
    ImageModule,
    TagModule,
    ProgressBarModule,
    ChatTableComponent,
    ChatChartComponent,
    ChatLocationComponent,
    ChatContactComponent,
    ChatImageComponent,
    ChatAudioComponent,
    ChatVideoComponent,
    TranslateModule
  ],
  templateUrl: './ai-broker-chat.component.html',
  styleUrls: ['./ai-broker-chat.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AiBrokerChatComponent implements OnDestroy, OnInit, AfterViewInit {
  @ViewChild('chatScrollPanel') private chatScrollPanel!: ElementRef;
  public readonly languageService = inject(LanguageService);

  readonly aiProfile = {
    fullName: 'LoadGate AI Agent',
    profilePictureUrl: 'assets/chatbot.png',
    status: 'Online - Replying now'
  };

  userProfile = signal<ChatParticipant | null>(null);

  canvasTitle = computed(() => {
    const profile = this.userProfile();
    if (!profile || !profile.userRole) return 'Item Details';

    const role = profile.userRole.toUpperCase();
    if (role === 'CUSTOMER') {
      return 'Load Request Details';
    } else if (role === 'DRIVER' || role === 'CARRIER' || role === 'FLEET_OWNER' || role === 'BROKER' || role === 'ADMIN') {
      return 'Activity Details';
    }
    return 'Item Details';
  });

  composerText = '';
  pendingFile?: File;
  pendingFilePreview?: string;
  pendingFileName?: string;
  activeCanvasPath = signal<string | null>(null);
  pendingAudioUrl?: string;
  pendingAudioDuration?: string;
  isRecording = false;
  recordingError = '';
  isAiTyping = signal(false);
  private recordingStart?: number;
  private mediaRecorder?: MediaRecorder;
  private readonly recordedChunks: Blob[] = [];
  private readonly objectUrls: string[] = [];
  private userSubscription: Subscription | undefined;
  uploadProgress = signal<number | null>(null);
  supportRequestInFlight = signal(false);

  private pendingAudioBlob?: Blob;
  isUploading = false;
  private supportQueryRequested = false;
  private supportRequestHandled = false;
  private supportRequestSource = 'NEXUS_HAUL_APPBAR';
  private canvasOpenSequence = 0;

  // Pagination for history
  currentPage = 0;
  pageSize = 20;
  hasMore = true;
  isLoadingMessages = signal(false);

  messages = signal<ChatMessageView[]>([]);

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly chatService: AiBrokerChatService,
    private readonly authService: AuthService,
    private readonly fcmService: FcmService,
    private readonly storageService: StorageService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly sessionStorageService: SessionStorageService
  ) {
    effect(() => {
      // Trigger scroll when typing starts/stops or messages change
      // but only if we're not currently loading historical messages
      if (!this.isLoadingMessages()) {
        this.messages();
        this.isAiTyping();
        this.scrollToBottom();
      }
    });
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      this.updateSupportRequestState(params);
    });

    this.userSubscription = this.authService.userData.subscribe((user: FirebaseUser | null) => {
      if (user) {
        const customer = this.sessionStorageService.getCustomer();
        // Assuming the first role or corporateRole represents the active role for header logic
        const userRole = customer?.corporateRole || customer?.roles?.[0]?.roleName || 'USER';

        this.userProfile.set({
          firebaseId: user.uid,
          fullName: user.displayName || user.email || 'Anonymous',
          profilePictureUrl: user.photoURL || undefined,
          role: 'USER',
          userRole: userRole,
          email: user.email || undefined
        });
        this.messages.set([]);
        this.currentPage = 0;
        this.hasMore = true;
        this.loadHistory(true); // Load initial history
        this.tryRequestHumanSupport();
      } else {
        this.userProfile.set(null);
        this.messages.set([]);
        this.currentPage = 0;
        this.hasMore = true;
      }
      this.cdr.markForCheck();
    });

    this.fcmService.requestPermission().then(token => {
      if (token) {
        this.fcmService.registerCurrentToken();
      }
    });

    this.fcmService.messages$.subscribe(payload => {
      console.log('AiBrokerChat: Received FCM message:', payload);
      if (payload.data) {
        try {
          const chatMessage = this.parseFcmPayload(payload.data);

          // Fallback: If textContent is missing in data but present in notification
          if (!chatMessage.textContent && payload.notification?.body) {
            chatMessage.textContent = payload.notification.body;
          }

          const hydrated = this.hydrateMessage(chatMessage);
          this.addMessageUniquely(hydrated);
          this.cdr.markForCheck();
          this.scrollToBottom();
          if (this.activeCanvasPath()) {
            this.reloadCanvas();
          }
        } catch (e) {
          console.error('Error handling FCM payload', e);
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.scrollToBottom(true);
    this.setupEntityClickListener();
  }

  ngOnDestroy(): void {
    this.userSubscription?.unsubscribe();
    this.objectUrls.forEach(url => URL.revokeObjectURL(url));
    if (this.mediaRecorder?.stream) {
      this.mediaRecorder.stream.getTracks().forEach(track => track.stop());
    }
  }

  canSend(): boolean {
    return (!!this.composerText.trim() || !!this.pendingFile || !!this.pendingAudioUrl) && !!this.userProfile() && !this.isUploading;
  }

  onComposerEnter(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (!keyboardEvent.shiftKey) {
      keyboardEvent.preventDefault();
      this.sendMessage();
    }
  }

  onScroll(): void {
    const element = this.chatScrollPanel.nativeElement;
    // When user scrolls to top and we have more history
    if (element.scrollTop < 50 && this.hasMore && !this.isLoadingMessages()) {
      this.loadHistory();
    }
  }

  loadHistory(isInitial: boolean = false): void {
    if (this.isLoadingMessages() || (!isInitial && !this.hasMore)) {
      return;
    }

    this.isLoadingMessages.set(true);
    this.cdr.markForCheck();

    const scrollContainer = this.chatScrollPanel?.nativeElement;
    const oldScrollHeight = scrollContainer?.scrollHeight || 0;

    this.chatService.getHistoryPaged(this.currentPage, this.pageSize)
      .pipe(finalize(() => {
        this.isLoadingMessages.set(false);
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: (page) => {
          const newMessages = (page.content || []).map((m: any) => this.hydrateMessage(m));

          if (isInitial) {
            this.messages.set([]);
            this.addMessagesUniquely(newMessages.reverse());
            this.scrollToBottom(true);
          } else {
            // Prepending messages
            this.prependMessagesUniquely(newMessages.reverse());

            // Maintain scroll position
            setTimeout(() => {
              const newScrollHeight = scrollContainer.scrollHeight;
              scrollContainer.scrollTop = newScrollHeight - oldScrollHeight;
            }, 0);
          }

          this.currentPage++;
          this.hasMore = !page.last;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Failed to load chat history:', err);
        }
      });
  }

  formatTime(date: Date | string): string {
    const parsed = typeof date === 'string' ? new Date(date) : date;
    return parsed.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  async startRecording(): Promise<void> {
    if (this.isRecording) {
      return;
    }

    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      this.recordingError = 'Voice capture is not available in this browser.';
      this.cdr.markForCheck();
      return;
    }

    if (typeof MediaRecorder === 'undefined') {
      this.recordingError = 'This browser cannot record audio. Try Chrome, Edge, or Safari.';
      this.cdr.markForCheck();
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.recordedChunks.length = 0;
      this.mediaRecorder = new MediaRecorder(stream);
      this.mediaRecorder.ondataavailable = event => {
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };
      this.mediaRecorder.onstop = () => this.finalizeRecording();
      this.mediaRecorder.start();
      this.recordingStart = performance.now();
      this.isRecording = true;
      this.recordingError = '';
      this.cdr.markForCheck();
    } catch (error) {
      this.recordingError = 'Microphone permission was denied.';
      this.cdr.markForCheck();
    }
  }

  stopRecording(): void {
    if (!this.isRecording || !this.mediaRecorder) {
      return;
    }

    this.mediaRecorder.stop();
    this.mediaRecorder.stream.getTracks().forEach(track => track.stop());
    this.isRecording = false;
    this.cdr.markForCheck();
  }

  cancelRecording(): void {
    if (this.mediaRecorder) {
      this.mediaRecorder.onstop = null;
      this.mediaRecorder.stop();
      this.mediaRecorder.stream.getTracks().forEach(track => track.stop());
    }
    this.isRecording = false;
    this.recordedChunks.length = 0;
    this.pendingAudioUrl = undefined;
    this.pendingAudioDuration = undefined;
    this.cdr.markForCheck();
  }

  toggleRecording(): void {
    if (this.isRecording) {
      this.stopRecording();
    } else {
      this.startRecording();
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    this.pendingFile = file;
    this.pendingFileName = file.name;

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        this.pendingFilePreview = reader.result as string;
        this.cdr.markForCheck();
      };
      reader.readAsDataURL(file);
    } else {
      this.pendingFilePreview = undefined;
      this.cdr.markForCheck();
    }
    input.value = '';
  }

  isPendingFileImage(): boolean {
    return !!this.pendingFile?.type.startsWith('image/');
  }

  removePendingFile(): void {
    this.pendingFile = undefined;
    this.pendingFilePreview = undefined;
    this.pendingFileName = undefined;
  }

  removePendingAudio(): void {
    if (this.pendingAudioUrl) {
      URL.revokeObjectURL(this.pendingAudioUrl);
    }
    this.pendingAudioUrl = undefined;
    this.pendingAudioDuration = undefined;
    this.pendingAudioBlob = undefined;
  }

  closeCanvas(): void {
    this.activeCanvasPath.set(null);
    this.router.navigate([{ outlets: { canvas: null } }], { relativeTo: this.route });
  }

  private navigateCanvas(path: string): void {
    const commands = path.split('/');
    this.router.navigate([{ outlets: { canvas: commands } }], { relativeTo: this.route });
  }

  private openMatchCanvasByReference(matchReference: string, fallbackId?: string, sequence?: number): void {
    this.chatService.resolveMatchCanvasReference(matchReference).subscribe({
      next: page => {
        if (!this.canApplyCanvasOpen(sequence)) {
          return;
        }
        const match = page.content?.[0];
        if (match?.id) {
          this.openCanvasPath(`matches/${match.id}`);
          return;
        }
        if (fallbackId) {
          this.openCanvasPath(`matches/${fallbackId}`);
        }
      },
      error: () => {
        if (!this.canApplyCanvasOpen(sequence)) {
          return;
        }
        if (fallbackId) {
          this.openCanvasPath(`matches/${fallbackId}`);
        }
      }
    });
  }

  private openCanvasFromMessage(msg: ChatMessageView): boolean {
    const sequence = ++this.canvasOpenSequence;
    const reference = this.pickCanvasReference(msg.textContent || '');
    if (reference) {
      if (reference.prefix === 'MTCH') {
        this.openMatchCanvasByReference(reference.fullRef, reference.id, sequence);
      } else {
        this.openCanvasPath(`${this.canvasRouteType(reference.prefix)}/${reference.routeToken}`);
      }
      return true;
    }

    if (msg.activeCanvasPath) {
      this.openCanvasPath(this.normalizeCanvasPath(msg.activeCanvasPath));
      return true;
    }
    return false;
  }

  private canApplyCanvasOpen(sequence?: number): boolean {
    return sequence === undefined || sequence === this.canvasOpenSequence;
  }

  private pickCanvasReference(text: string): CanvasReference | null {
    const references = this.extractCanvasReferences(text);
    if (!references.length) {
      return null;
    }
    const match = references.find(ref => ref.prefix === 'MTCH');
    if (match) {
      return match;
    }
    const hasTruckListing = /matching trucks/i.test(text);
    const hasLoadListing = /matching loads/i.test(text);
    if (hasTruckListing) {
      return references.find(ref => ref.prefix === 'TRK') || references[0];
    }
    if (hasLoadListing) {
      return references.find(ref => ref.prefix === 'REQ') || references[0];
    }
    return references.find(ref => ref.prefix === 'SHP')
      || references.find(ref => ref.prefix === 'TRK')
      || references.find(ref => ref.prefix === 'REQ')
      || references[0];
  }

  private extractCanvasReferences(text: string): CanvasReference[] {
    const references: CanvasReference[] = [];
    const entityRegex = /((TRK|REQ|SHP|MTCH)\/(?:(\d{4}\/\d{2})\/)?(\d+))/g;
    let match: RegExpExecArray | null;
    while ((match = entityRegex.exec(text)) !== null) {
      references.push({
        fullRef: match[1],
        prefix: match[2] as CanvasReferencePrefix,
        id: match[4],
        routeToken: this.toCanvasReferenceToken(match[1])
      });
    }
    return references;
  }

  private canvasRouteType(prefix: CanvasReferencePrefix): string {
    if (prefix === 'TRK') return 'availability-details';
    if (prefix === 'SHP') return 'shipments';
    if (prefix === 'MTCH') return 'matches';
    return 'load-details';
  }

  private setupEntityClickListener(): void {
    if (this.chatScrollPanel) {
      this.chatScrollPanel.nativeElement.addEventListener('click', (event: MouseEvent) => {
        const target = (event.target as HTMLElement).closest('.entity-link') as HTMLElement;
        if (target) {
          const type = target.getAttribute('data-type');
          const id = target.getAttribute('data-id');
          const fullRef = target.getAttribute('data-ref');
          const prefix = target.getAttribute('data-prefix') as CanvasReferencePrefix | null;
          if (type && id) {
            this.handleEntityClick(type, id, fullRef || undefined, prefix || undefined);
          }
        }
      });
    }
  }

  private handleEntityClick(type: string, id: string, fullRef?: string, prefix?: CanvasReferencePrefix): void {
    if (prefix === 'MTCH' && fullRef) {
      this.openMatchCanvasByReference(fullRef, id);
      return;
    }

    const path = `${type}/${id}`;
    this.openCanvasPath(path);
  }

  private openCanvasPath(path: string): void {
    // If it's already the active path, force a refresh/reload
    if (this.activeCanvasPath() === path) {
      this.reloadCanvas();
    } else {
      this.activeCanvasPath.set(path);
      this.navigateCanvas(path);
    }
    this.cdr.markForCheck();
  }

  reloadCanvas(): void {
    const path = this.activeCanvasPath();
    if (path) {
      // Force reload by briefly navigating away or using a flag
      // For now, re-triggering the navigation is the standard approach
      this.navigateCanvas(path);

      // Notify canvas components to re-fetch data
      this.chatService.triggerCanvasRefresh();

      // We can also trigger a CDR cycle to be sure
      this.cdr.markForCheck();
    }
  }

  async sendMessage(): Promise<void> {
    if (!this.canSend() || !this.userProfile() || this.isUploading) {
      return;
    }

    this.isUploading = true;
    this.recordingError = '';
    this.cdr.markForCheck();

    try {
      let imageContent: ChatAttachment[] | undefined = undefined;
      let audioContent: ChatAttachment[] | undefined = undefined;
      let videoContent: ChatAttachment[] | undefined = undefined;
      let attachmentContent: ChatAttachment[] | undefined = undefined;

      // Handle File Upload
      if (this.pendingFile) {
        const type = this.pendingFile.type;
        let folder = 'others';
        if (type.startsWith('image/')) folder = 'images';
        else if (type.startsWith('audio/')) folder = 'audio';
        else if (type.startsWith('video/')) folder = 'video';

        const path = `chat/${folder}/${Date.now()}_${this.pendingFile.name}`;
        const { task, url } = this.storageService.uploadFileWithProgress(path, this.pendingFile);

        task.subscribe(snap => {
          const prog = (snap.bytesTransferred / snap.totalBytes) * 100;
          this.uploadProgress.set(Math.round(prog));
          this.cdr.markForCheck();
        });

        const downloadUrl = await firstValueFrom(url);

        const att: ChatAttachment = {
          fileUrl: downloadUrl,
          fileName: this.pendingFileName || this.pendingFile.name,
          fileType: type,
          fileSize: this.pendingFile.size
        };

        if (type.startsWith('image/')) imageContent = [att];
        else if (type.startsWith('audio/')) audioContent = [att];
        else if (type.startsWith('video/')) videoContent = [att];
        else attachmentContent = [att];
      }

      // Handle Voice Recording Upload
      if (this.pendingAudioBlob) {
        const path = `chat/audio/${Date.now()}.webm`;
        const { task, url } = this.storageService.uploadFileWithProgress(path, this.pendingAudioBlob);

        task.subscribe(snap => {
          const prog = (snap.bytesTransferred / snap.totalBytes) * 100;
          this.uploadProgress.set(Math.round(prog));
          this.cdr.markForCheck();
        });

        const downloadUrl = await firstValueFrom(url);
        const voiceAtt: ChatAttachment = {
          fileUrl: downloadUrl,
          fileName: 'Voice Message',
          fileType: 'audio/webm',
          fileSize: this.pendingAudioBlob.size
        };

        if (audioContent) audioContent.push(voiceAtt);
        else audioContent = [voiceAtt];
      }

      const textContent = this.composerText.trim() || undefined;

      const newMessageBase: ChatMessage = {
        messageId: Date.now(),
        success: true,
        partial: false,
        sender: {
          firebaseId: this.userProfile()!.firebaseId,
          fullName: this.userProfile()!.fullName,
          profilePictureUrl: this.userProfile()!.profilePictureUrl,
          role: 'USER'
        },
        textContent,
        imageContent,
        audioContent,
        videoContent,
        attachmentContent,
        timestamp: new Date(),
        status: 'Read',
        isUserMessage: true
      };

      const newMessage: ChatMessageView = {
        ...newMessageBase,
        contents: this.buildContentsFromPayload(newMessageBase)
      };

      this.addMessageUniquely(newMessage);

      // Cleanup
      this.composerText = '';
      this.pendingFile = undefined;
      this.pendingFilePreview = undefined;
      this.pendingFileName = undefined;
      this.pendingAudioUrl = undefined;
      this.pendingAudioDuration = undefined;
      this.pendingAudioBlob = undefined;

      this.queueAiResponse(newMessage);
      this.scrollToBottom(true);
    } catch (error) {
      console.error('Error sending message:', error);
      this.recordingError = 'Failed to upload attachments. Please try again.';
    } finally {
      this.isUploading = false;
      this.uploadProgress.set(null);
      this.cdr.markForCheck();
    }
  }

  get timelineLabel(): string {
    const date = new Date();
    return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  }

  private scrollToBottom(force: boolean = false): void {
    // Use a timeout to ensure the DOM has been updated with the new message
    // and the scroll panel has had time to update its internal state.
    setTimeout(() => {
      try {
        if (!this.chatScrollPanel) {
          return;
        }
        const scrollContainer = this.chatScrollPanel.nativeElement;
        const scrollThreshold = 200; // Increased threshold

        const atBottom = scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight <= scrollThreshold;

        if (force || atBottom) {
          scrollContainer.scrollTo({
            top: scrollContainer.scrollHeight,
            behavior: force ? 'auto' : 'smooth'
          });
        }
      } catch (err) {
        console.warn('Could not scroll to bottom:', err);
      }
    }, 100); // Increased timeout for better stability
  }

  private finalizeRecording(): void {
    if (!this.recordedChunks.length) {
      return;
    }

    const blob = new Blob(this.recordedChunks, { type: 'audio/webm' });
    this.pendingAudioBlob = blob;
    const url = URL.createObjectURL(blob);
    this.objectUrls.push(url);
    const durationMs = this.recordingStart ? performance.now() - this.recordingStart : 0;
    this.pendingAudioDuration = this.formatDuration(durationMs);
    this.pendingAudioUrl = url;
    this.cdr.markForCheck();
  }

  private queueAiResponse(userMessage: ChatMessage): void {
    this.isAiTyping.set(true);
    const payload: ChatMessage = {
      ...userMessage,
      requestMessageId: userMessage.messageId
    };

    this.chatService
      .chat(payload)
      .pipe(
        finalize(() => {
          this.isAiTyping.set(false);
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          const hydrated = this.hydrateMessage(response);
          this.addMessageUniquely(hydrated);
          this.cdr.markForCheck();
          this.scrollToBottom(true);
          if (this.activeCanvasPath()) {
            this.reloadCanvas();
          }
        },
        error: () => {
          const fallback = this.buildAiResponse(userMessage as ChatMessageView);
          this.addMessageUniquely(fallback);
          this.cdr.markForCheck();
          this.scrollToBottom(true);
        }
      });
  }

  private buildAiResponse(userMessage: ChatMessageView): ChatMessageView {
    const hasImage = userMessage.contents.some(content => content.kind === 'image');
    const hasAudio = userMessage.contents.some(content => content.kind === 'audio');
    const intent = userMessage.contents.find(
      (content): content is Extract<ChatContent, { kind: 'text' }> => content.kind === 'text'
    )?.text;

    const summary: string[] = [];
    summary.push("I've parsed your update.");
    if (hasImage) {
      summary.push("The photo looks like mixed pallets stacked on a wooden frame; I'll flag it for uneven loads.");
    }
    if (hasAudio) {
      summary.push('I transcribed the voice note and added the handling instructions to the load record.');
    }
    if (intent) {
      summary.push(`Key takeaway: ${intent.slice(0, 120)}${intent.length > 120 ? '...' : ''}`);
    }

    return {
      messageId: Date.now(),
      success: true,
      partial: false,
      sender: {
        fullName: this.aiProfile.fullName,
        profilePictureUrl: this.aiProfile.profilePictureUrl,
        role: 'AI'
      },
      textContent: summary.join(' '),
      timestamp: new Date(),
      status: 'Read',
      contents: [
        {
          kind: 'text',
          text: summary.join(' ')
        }
      ]
    };
  }

  private formatDuration(milliseconds: number): string {
    const totalSeconds = Math.max(1, Math.round(milliseconds / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  private hydrateMessage(payload: ChatMessage): ChatMessageView {
    const timestamp = payload.timestamp ? new Date(payload.timestamp) : new Date();

    // Reconstruct sender if it's a flat record from history
    let sender = payload.sender;

    // Use payload properties if sender is missing
    if (!sender) {
      sender = {
        fullName: this.aiProfile.fullName,
        profilePictureUrl: this.aiProfile.profilePictureUrl,
        role: 'AI'
      };
    }

    const contents = this.buildContentsFromPayload(payload);

    return {
      ...payload,
      success: true,
      partial: payload.partial ?? false,
      sender,
      timestamp,
      contents
    };
  }

  protected isAiSender(message: ChatMessageView): boolean {
    return !this.isUserSender(message);
  }

  protected isUserSender(message: ChatMessageView): boolean {
    const isUser = this.isTrue(message.isUserMessage);
    if (message.isUserMessage !== undefined && message.isUserMessage !== null) {
      return isUser;
    }

    // Priority 2: Match Firebase IDs
    const currentFirebaseId = this.userProfile()?.firebaseId;
    if (currentFirebaseId && message.sender?.firebaseId) {
      return currentFirebaseId === message.sender.firebaseId;
    }

    // Priority 3: Falling back to roles
    const role = (message.sender?.role || '').toUpperCase();
    const userRole = (message.sender?.userRole || '').toUpperCase();
    const humanRoles = ['USER', 'CUSTOMER', 'DRIVER', 'BROKER', 'ADMIN', 'AGENT', 'GUEST', 'HUMAN'];

    return humanRoles.includes(role) || humanRoles.includes(userRole);
  }

  private isTrue(value: any): boolean {
    if (value === undefined || value === null) return false;
    return value === true || value === 'true' || value === 1 || value === '1';
  }

  private updateSupportRequestState(params: import('@angular/router').ParamMap): void {
    const supportFlag = params.get('support') === '1';
    if (!supportFlag) {
      this.supportQueryRequested = false;
      this.supportRequestHandled = false;
      this.supportRequestSource = 'NEXUS_HAUL_APPBAR';
      return;
    }

    this.supportQueryRequested = true;
    this.supportRequestHandled = false;
    this.supportRequestSource = this.normalizeSupportSource(params.get('source'));
    this.tryRequestHumanSupport();
  }

  private tryRequestHumanSupport(): void {
    if (!this.supportQueryRequested || this.supportRequestHandled || this.supportRequestInFlight() || !this.userProfile()) {
      return;
    }

    this.supportRequestHandled = true;
    this.supportRequestInFlight.set(true);

    const payload: HumanSupportRequestPayload = {
      source: this.supportRequestSource,
      currentPath: this.router.url.split('?')[0]
    };

    this.chatService.requestHumanSupport(payload)
      .pipe(finalize(() => {
        this.supportRequestInFlight.set(false);
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: () => {
          this.currentPage = 0;
          this.hasMore = true;
          this.messages.set([]);
          this.loadHistory(true);
          this.clearSupportQueryParams();
        },
        error: (error) => {
          console.error('Failed to request human support:', error);
          this.supportRequestHandled = false;
        }
      });
  }

  private clearSupportQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        support: null,
        source: null
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private normalizeSupportSource(source: string | null): string {
    if (!source) {
      return 'NEXUS_HAUL_APPBAR';
    }

    if (source.toLowerCase() === 'appbar') {
      return 'NEXUS_HAUL_APPBAR';
    }

    return source.toUpperCase();
  }




  protected isText(content: ChatContent): content is Extract<ChatContent, { kind: 'text' }> {
    return content.kind === 'text';
  }

  protected isImage(content: ChatContent): content is Extract<ChatContent, { kind: 'image' }> {
    return content.kind === 'image';
  }

  protected isAudio(content: ChatContent): content is Extract<ChatContent, { kind: 'audio' }> {
    return content.kind === 'audio';
  }

  protected isVideo(content: ChatContent): content is Extract<ChatContent, { kind: 'video' }> {
    return content.kind === 'video';
  }

  protected isTable(content: ChatContent): content is Extract<ChatContent, { kind: 'table' }> {
    return content.kind === 'table';
  }

  protected isChart(content: ChatContent): content is Extract<ChatContent, { kind: 'chart' }> {
    return content.kind === 'chart';
  }

  protected isAttachment(content: ChatContent): content is Extract<ChatContent, { kind: 'attachment' }> {
    return content.kind === 'attachment';
  }

  protected isBusinessObject(content: ChatContent): content is Extract<ChatContent, { kind: 'business-object' }> {
    return content.kind === 'business-object';
  }

  protected isLocation(content: ChatContent): content is Extract<ChatContent, { kind: 'location' }> {
    return content.kind === 'location';
  }

  protected isContact(content: ChatContent): content is Extract<ChatContent, { kind: 'contact' }> {
    return content.kind === 'contact';
  }

  protected isActionButtons(content: ChatContent): content is Extract<ChatContent, { kind: 'action-buttons' }> {
    return content.kind === 'action-buttons';
  }

  onActionButtonClick(button: ChatActionButton): void {
    if (button.action === 'SEND_MESSAGE') {
      this.composerText = button.payload;
      this.sendMessage();
    } else if (button.action === 'OPEN_URL') {
      window.open(button.payload, '_blank');
    } else if (button.action === 'NAVIGATE') {
      this.router.navigateByUrl(button.payload);
    }
  }

  private buildContentsFromPayload(payload: Partial<ChatMessage>): ChatContent[] {
    const contents: ChatContent[] = [];

    if (payload.textContent) {
      contents.push({ kind: 'text', text: payload.textContent });
    }

    if (payload.imageContent && Array.isArray(payload.imageContent)) {
      payload.imageContent.forEach(img => {
        if (img.fileUrl) {
          contents.push({ kind: 'image', data: { url: img.fileUrl, alt: img.fileName, name: img.fileName } });
        }
      });
    }

    if (payload.audioContent && Array.isArray(payload.audioContent)) {
      payload.audioContent.forEach(aud => {
        if (aud.fileUrl) {
          contents.push({ kind: 'audio', data: { url: aud.fileUrl, name: aud.fileName } });
        }
      });
    }

    if (payload.videoContent && Array.isArray(payload.videoContent)) {
      payload.videoContent.forEach(vid => {
        if (vid.fileUrl) {
          contents.push({ kind: 'video', data: { url: vid.fileUrl, alt: vid.fileName, name: vid.fileName } });
        }
      });
    }

    if (payload.tableContent && Array.isArray(payload.tableContent)) {
      payload.tableContent.forEach(table => {
        contents.push({ kind: 'table', data: table });
      });
    }

    if (payload.chartContent && Array.isArray(payload.chartContent)) {
      payload.chartContent.forEach(chart => {
        contents.push({ kind: 'chart', data: chart });
      });
    }

    if (payload.locationContent && Array.isArray(payload.locationContent)) {
      payload.locationContent.forEach(loc => {
        contents.push({ kind: 'location', data: loc });
      });
    }

    if (payload.contactContent && Array.isArray(payload.contactContent)) {
      payload.contactContent.forEach(con => {
        contents.push({ kind: 'contact', data: con });
      });
    }

    if (payload.attachmentContent && Array.isArray(payload.attachmentContent)) {
      payload.attachmentContent.forEach(att => {
        if (att.fileUrl) {
          contents.push({
            kind: 'attachment',
            name: att.fileName || 'Attachment',
            url: att.fileUrl,
            mimeType: att.fileType,
            sizeLabel: att.fileSize ? `${(att.fileSize / 1024).toFixed(1)} KB` : undefined
          });
        }
      });
    }

    if (payload.businessObject) {
      contents.push({ kind: 'business-object', data: payload.businessObject });
    }

    if (payload.actionButtons && Array.isArray(payload.actionButtons) && payload.actionButtons.length > 0) {
      contents.push({ kind: 'action-buttons', data: payload.actionButtons });
    }

    return contents;
  }



  // Renamed and refactored to produce segments
  convertToHtml(rawText: string): string {
    let text = rawText;
    // 1. Headings (H2)
    text = text.replace(/## (.+)/g, "<h2>$1</h2>");

    // 2. Bold text (convert to strong for semantic correctness)
    text = text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");

    // 3. Delivery Partner List (handling * as list items)
    text = text.replace(/\* (.+?) \*/g, "<ul><li>$1</li></ul>"); //For single list

    text = text.replace(/\* (.+)/g, "<li>$1</li>"); //For multiple list

    if (text.includes("<li>")) {
      const listItems = text.split("\n").filter(line => line.startsWith("<li>"));
      if (listItems.length > 1) {
        text = text.replace(/\* (.+)/g, ""); // clear old list
        text = text.replace(listItems.join(""), `<ul>${listItems.join("")}</ul>`); // wrap all <li> to <ul>
      }
    }

    // 5. Code Blocks
    const codeBlockRegex = /```(\w+)?\n([\s\S]+?)\n```/g;
    text = text.replace(codeBlockRegex, "");

    // 6. Images
    const imageRegex = /!\[(.*?)\]\((.*?)\)/g;
    text = text.replace(imageRegex, (match, alt, url) => {
      return `<img src="${url}" alt="${alt}" style="max-width: 100%; height: auto;">`;
    });

    // 7. Links (Markdown)
    const linkRegex = /\[(.*?)\]\((.*?)\)/g;
    text = text.replace(linkRegex, (match, linkText, url) => {
      return `<a href="${url}" target="_blank">${linkText}</a>`;
    });

    // 8. Links (Plain URLs)
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    text = text.replace(urlRegex, (match, url) => {
      return `<a href="${url}" target="_blank">${url}</a>`;
    });

    // 9. Blockquotes
    const blockquoteRegex = /^>(.*)$/gm; // 'gm' for multiline
    text = text.replace(blockquoteRegex, (match, quote) => {
      return `<blockquote>${quote}</blockquote>`;
    });

    // 10. Horizontal Rules
    const hrRegex = /^(---|(\*\*\*))$/gm;
    text = text.replace(hrRegex, '<hr>');

    // 11. File Upload
    const fileRegex = /\[(.*?)\]\((.*?)\)/g;
    text = text.replace(fileRegex, (match, fileName, fileUrl) => {
      return `<a href="${fileUrl}" target="_blank" class="file-link">${fileName}</a>`;
    });

    // 12. Newlines to <br> for other text (except headings and lists which are already block elements)
    text = text.replace(/(?<!<h.>)(?<!<ul.>)(?<!<\/ul>)(?<!<table.+)(?<!<\/table>)(?<!<pre.>)(?<!<\/pre>)(?<!<img.+)(?<!<a.+)(?<!<\/a>)(?<!<blockquote.+)(?<!<\/blockquote>)\n/g, "<br>");

    // 13. Entity References (TRK, REQ, SHP, MTCH)
    // Matches patterns like TRK/2026/02/00000843 or REQ/00000841
    const entityRegex = /((TRK|REQ|SHP|MTCH)\/(?:(\d{4}\/\d{2})\/)?(\d+))/g;
    text = text.replace(entityRegex, (match, fullRef, typePrefix, datePart, id) => {
      const type = this.canvasRouteType(typePrefix as CanvasReferencePrefix);

      return `<span class="entity-link" data-type="${type}" data-id="${this.toCanvasReferenceToken(fullRef)}" data-prefix="${typePrefix}" data-ref="${fullRef}" title="View Details">${fullRef}</span>`;
    });

    return text;
  }

  private toCanvasReferenceToken(reference: string): string {
    return reference.replace(/\//g, '-');
  }

  private normalizeCanvasPath(path: string): string {
    const referencePath = /^(load-details|availability-details|shipments|matches)\/((?:REQ|TRK|SHP|MTCH)\/.+)$/i.exec(path);
    if (!referencePath) {
      return path;
    }
    return `${referencePath[1]}/${this.toCanvasReferenceToken(referencePath[2])}`;
  }

  private addMessageUniquely(msg: ChatMessageView): void {
    this.openCanvasFromMessage(msg);

    if (!msg.messageId) {
      this.messages.update(prev => [...prev, msg]);
      return;
    }
    const index = this.messages().findIndex(m => m.messageId === msg.messageId);
    if (index === -1) {
      this.messages.update(prev => [...prev, msg]);
    } else {
      // If found, we can optionally update it if the new one has more info
      this.messages.update(prev => {
        const next = [...prev];
        next[index] = { ...next[index], ...msg };
        return next;
      });
    }
  }

  private addMessagesUniquely(msgs: ChatMessageView[]): void {
    msgs.forEach(m => this.addMessageUniquely(m));
  }

  private prependMessagesUniquely(newMsgs: ChatMessageView[]): void {
    const existingIds = new Set(this.messages().map(m => m.messageId).filter(id => !!id));
    const uniqueNew = newMsgs.filter(nm => !nm.messageId || !existingIds.has(nm.messageId));
    this.messages.update(prev => [...uniqueNew, ...prev]);
  }

  private parseFcmPayload(data: any): ChatMessage {
    // 1. Check for single JSON string
    if (data['message']) {
      return JSON.parse(data['message']);
    }

    // 2. Handle flattened data fields
    const raw = data as Record<string, any>;

    // Helper to parse potential stringified JSON or return default
    const tryParse = (val: any) => {
      if (typeof val === 'string' && (val.startsWith('{') || val.startsWith('['))) {
        try { return JSON.parse(val); } catch { return val; }
      }
      return val;
    };

    const toNumber = (val: any) => (val !== undefined && val !== null) ? Number(val) : undefined;
    const toBool = (val: any) => val === true || val === 'true' || val === '1' || val === 1;

    return {
      messageId: toNumber(raw['messageId']),
      sessionId: toNumber(raw['sessionId']),
      requestMessageId: toNumber(raw['requestMessageId']),
      success: toBool(raw['success']),
      partial: toBool(raw['partial'] || raw['isPartial']),
      sender: tryParse(raw['sender']),
      activeCanvasPath: raw['activeCanvasPath'],
      stateContext: tryParse(raw['stateContext']),
      textContent: raw['textContent'],
      tableContent: tryParse(raw['tableContent']),
      chartContent: tryParse(raw['chartContent']),
      locationContent: tryParse(raw['locationContent']),
      contactContent: tryParse(raw['contactContent']),
      attachmentContent: tryParse(raw['attachmentContent']),
      imageContent: tryParse(raw['imageContent']),
      videoContent: tryParse(raw['videoContent']),
      audioContent: tryParse(raw['audioContent']),
      businessObject: tryParse(raw['businessObject']),
      actionButtons: tryParse(raw['actionButtons']),
      timestamp: raw['timestamp'] ? new Date(raw['timestamp']) : new Date(),
      status: raw['status'],
      isUserMessage: toBool(raw['isUserMessage'])
    } as ChatMessage;
  }
}
